// Crop-back (photohawk-serverless docs/preview-first-uploads.md, "Crop-back"): the background track that
// gives the server its face / person crops cut from the ORIGINALS of the preview copies we uploaded.
//
// The server detects on the small copy (detectors work at 640px) and, for a copy minted with cropBack,
// holds the steps that use the crops (face indexing, bib OCR) and asks for each detection cut from the
// original. This track:
//   • remembers each copy that landed (contentGuid → its original File);
//   • polls POST /admin/content {cropBack:{poll, done}} — ONE call per gallery every few seconds for up to
//     100 photos, which also carries the reports of what it finished since the last poll;
//   • for each photo with crops ready, cuts them from the original on the resize pool (cropCore: the
//     server's geometry, EXIF-upright, decoded only as large as needed), PUTs each to its presigned target
//     and reports {contentGuid, faces|people: 'ok'} — or 'skip' when the original can't help.
//
// Invisible and fail-safe: nothing waits on it. Whatever it doesn't deliver within the server's hold, the
// server does from the copy (and from the original once that lands). It never retries a cut, and it lets
// a photo go after MAX_TRACK_MS. Off with localStorage ph_crop_back=0.

import type { CropRequest, CutCrop } from "@/lib/image/cropCore";
import { readJpegHeader } from "@/lib/image/scaledDecode";

type Kind = "faces" | "people";
const KINDS: Kind[] = ["faces", "people"];

export interface CropBackItem extends CropRequest {
  i: number;
  key: string;
  url: string;
  /** Upload-Worker target for this crop (when the poll asked with uploadVia:'worker'). */
  worker?: { url: string; token: string };
}
export interface BundleTarget {
  url: string;
  maxEntries: number;
  maxBytes: number;
}
interface KindView {
  state: string;
  items?: CropBackItem[];
}
export interface CropBackPhoto {
  contentGuid: string;
  frame?: { width: number; height: number } | null;
  expect?: Kind[];
  faces?: KindView;
  people?: KindView;
}
export interface CropBackDone {
  contentGuid: string;
  faces?: "ok" | "skip";
  people?: "ok" | "skip";
}
export interface PollResponse {
  cropBack?: boolean;
  photos?: CropBackPhoto[];
  pollMs?: number;
  /** The upload Worker's bundle endpoint: all of a photo's crops in one request. */
  bundle?: BundleTarget;
}

/** What the track needs from the outside world — injected so it is testable without a browser. */
export interface CropBackIO {
  poll: (engineGuid: string, guids: string[], done: CropBackDone[]) => Promise<PollResponse>;
  cut: (file: File, items: CropRequest[]) => Promise<CutCrop[] | null>;
  put: (url: string, blob: Blob) => Promise<boolean>;
  /** Several crops in one Worker bundle; resolves per entry (false = re-send it with `put`). */
  putBundle?: (target: BundleTarget, entries: Array<{ token: string; blob: Blob }>) => Promise<boolean[]>;
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
  log?: (...a: unknown[]) => void;
  /** Tests drive cycle() themselves instead of the background loop. */
  manual?: boolean;
}

const POLL_MS = 4000;
const MAX_POLL = 100; // the server reads up to 100 records per poll
const MAX_TRACK_MS = 6 * 60 * 1000; // past the server's hold (150 s) + slack: it has fallen back by then
const PUT_CONC = 4;
// Server states after which nothing more will be asked for a kind.
const FINAL = new Set(["none", "applying", "applied", "fallback", "late", "expired"]);

interface Entry {
  engineGuid: string;
  contentGuid: string;
  file: File;
  since: number;
  /** Kinds already cut (or skipped) — never cut twice. */
  handled: Set<Kind>;
  /** Kinds the server has finished with. */
  final: Set<Kind>;
}

export const cropBackWanted = (): boolean => {
  try {
    return globalThis.localStorage?.getItem("ph_crop_back") !== "0";
  } catch {
    return true;
  }
};

const mapLimit = async <T, R>(xs: T[], n: number, fn: (x: T, i: number) => Promise<R>): Promise<R[]> => {
  const out = new Array<R>(xs.length);
  let next = 0;
  await Promise.all(Array.from({ length: Math.min(n, xs.length) }, async () => {
    while (next < xs.length) {
      const i = next++;
      out[i] = await fn(xs[i], i);
    }
  }));
  return out;
};

export interface CropBackStats {
  tracked: number;
  bundles: number;
  cut: number;
  crops: number;
  skipped: number;
  bytes: number;
}

/** One track per page; a scope's galleries are polled separately (one call per gallery per cycle). */
export class CropBackTrack {
  private entries = new Map<string, Entry>();
  private done = new Map<string, CropBackDone[]>(); // engineGuid → reports for the next poll
  private running = false;
  private off = false;
  private bundle: BundleTarget | null = null;
  readonly stats: CropBackStats = { tracked: 0, bundles: 0, cut: 0, crops: 0, skipped: 0, bytes: 0 };

  constructor(private io: CropBackIO) {}

  private now() { return (this.io.now || Date.now)(); }

  get size() { return this.entries.size; }

  /** The photo's ORIGINAL has been uploaded: the server cuts whatever it still needs from it itself
   *  (lib cropBack.onOriginal), so stop watching it — no decode, no more polling for it. */
  untrack(contentGuid: string | null | undefined) {
    if (contentGuid) this.entries.delete(contentGuid);
  }

  /** A copy minted with cropBack has landed: watch it until the server is done with its crops. */
  track(engineGuid: string, contentGuid: string | null | undefined, original: File) {
    if (this.off || !contentGuid || !engineGuid || this.entries.has(contentGuid)) return;
    this.entries.set(contentGuid, { engineGuid, contentGuid, file: original, since: this.now(), handled: new Set(), final: new Set() });
    this.stats.tracked += 1;
    if (!this.running && !this.io.manual) void this.loop();
  }

  private report(e: Entry, kind: Kind, how: "ok" | "skip") {
    const list = this.done.get(e.engineGuid) || [];
    let d = list.find((x) => x.contentGuid === e.contentGuid);
    if (!d) list.push((d = { contentGuid: e.contentGuid }));
    d[kind] = how;
    this.done.set(e.engineGuid, list);
  }

  private async loop() {
    this.running = true;
    const sleep = this.io.sleep || ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
    try {
      while (!this.off && (this.entries.size || this.done.size)) {
        const wait = await this.cycle();
        if (this.entries.size || this.done.size) await sleep(wait);
      }
    } finally {
      this.running = false;
    }
  }

  /** One poll per gallery, then cut whatever is ready. Returns how long to wait before the next one. */
  async cycle(): Promise<number> {
    const now = this.now();
    for (const [g, e] of this.entries) if (now - e.since > MAX_TRACK_MS) this.entries.delete(g);
    const galleries = new Set([...[...this.entries.values()].map((e) => e.engineGuid), ...this.done.keys()]);
    let pollMs = POLL_MS;
    for (const engineGuid of galleries) {
      const mine = [...this.entries.values()].filter((e) => e.engineGuid === engineGuid).sort((a, b) => a.since - b.since);
      const guids = mine.slice(0, MAX_POLL).map((e) => e.contentGuid);
      const done = this.done.get(engineGuid) || [];
      this.done.delete(engineGuid);
      let res: PollResponse;
      try {
        res = await this.io.poll(engineGuid, guids, done);
      } catch {
        // Transient: put the reports back for the next poll (they are idempotent server-side).
        if (done.length) this.done.set(engineGuid, [...done, ...(this.done.get(engineGuid) || [])]);
        continue;
      }
      if (res && res.cropBack === false) {
        // The stage can't do crop-back: stop — the server scans exactly as without it.
        this.off = true;
        this.entries.clear();
        this.done.clear();
        return pollMs;
      }
      if (res && res.pollMs) pollMs = Math.max(1000, res.pollMs);
      this.bundle = (res && res.bundle) || null;
      const ready: Array<{ e: Entry; kinds: Kind[]; items: CropBackItem[]; byKind: Map<Kind, CropBackItem[]>; photo: CropBackPhoto }> = [];
      for (const photo of (res && res.photos) || []) {
        const e = this.entries.get(photo.contentGuid);
        if (!e) continue;
        const expect = photo.expect && photo.expect.length ? photo.expect : KINDS.filter((k) => photo[k]);
        const byKind = new Map<Kind, CropBackItem[]>();
        for (const k of expect) {
          const v = photo[k];
          if (!v) continue;
          if (FINAL.has(v.state)) e.final.add(k);
          else if (v.state === "ready" && !e.handled.has(k) && v.items && v.items.length) byKind.set(k, v.items);
        }
        if (expect.every((k) => e.final.has(k))) {
          this.entries.delete(e.contentGuid);
          continue;
        }
        if (byKind.size) ready.push({ e, kinds: [...byKind.keys()], items: [...byKind.values()].flat(), byKind, photo });
      }
      // One original at a time: decoding originals is the expensive part, and it shares the resize
      // pool with the copies still being made. The PUTs for a photo go in parallel.
      for (const r of ready) await this.cutOne(r.e, r.kinds, r.byKind, r.photo);
    }
    return pollMs;
  }

  private async cutOne(e: Entry, kinds: Kind[], byKind: Map<Kind, CropBackItem[]>, photo: CropBackPhoto) {
    for (const k of kinds) e.handled.add(k);
    const skip = () => { this.stats.skipped += 1; for (const k of kinds) this.report(e, k, "skip"); };
    // Nothing to gain when the original is no bigger than the copy the server cropped from.
    const header = await readJpegHeader(e.file);
    const frame = photo.frame;
    if (!header || (frame && Math.max(header.width, header.height) <= Math.max(frame.width, frame.height))) return skip();
    const items = kinds.flatMap((k) => byKind.get(k) || []);
    let crops: CutCrop[] | null = null;
    try {
      crops = await this.io.cut(e.file, items.map(({ rect, maxEdge, quality }) => ({ rect, maxEdge, quality })));
    } catch {
      crops = null;
    }
    if (!crops || crops.length !== items.length) return skip();
    // All of this photo's crops in ONE Worker bundle when offered (one round trip instead of one per
    // crop); whatever it didn't ack — or everything, without a bundle — goes as single presigned PUTs.
    let viaBundle: boolean[] = items.map(() => false);
    const b = this.bundle;
    if (b && this.io.putBundle && items.length > 1 && items.length <= b.maxEntries && items.every((it) => it.worker)
      && crops.reduce((n, c) => n + c.blob.size, 0) < b.maxBytes) {
      try {
        viaBundle = await this.io.putBundle(b, items.map((it, i) => ({ token: it.worker!.token, blob: crops![i].blob })));
        this.stats.bundles += 1;
      } catch {
        viaBundle = items.map(() => false);
      }
    }
    const ok = await mapLimit(items, PUT_CONC, async (it, i) => {
      const put = viaBundle[i] || (await this.io.put(it.url, crops![i].blob).catch(() => false));
      if (put) { this.stats.crops += 1; this.stats.bytes += crops![i].blob.size; }
      return put;
    });
    this.stats.cut += 1;
    // Per kind: 'ok' if any of its crops landed (the server checks each one and keeps its own for the rest).
    let at = 0;
    for (const k of kinds) {
      const n = (byKind.get(k) || []).length;
      this.report(e, k, ok.slice(at, at + n).some(Boolean) ? "ok" : "skip");
      at += n;
    }
    this.io.log?.("[crop-back]", e.contentGuid, kinds.join("+"), `${items.length} crop(s)`);
  }
}
